/**
 * Resource Types admin page (GDN-1920 slice).
 *
 * Lists every resource type registered with the Resource Access registry
 * and renders the Pydantic JSON Schema contract for the selected type —
 * a labeled field table plus a raw JSON toggle. Instance browsing
 * (list / detail per type) is the follow-on Resource Explorer work.
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import {
  useGetResourceTypeSchemaQuery,
  useListResourceTypesQuery,
} from '../store/api';

type JsonSchemaNode = Record<string, any>;

function refName(ref: string): string {
  return ref.split('/').pop() ?? ref;
}

/** Human-readable type label for one JSON Schema property node. */
function typeLabel(node: JsonSchemaNode | undefined): string {
  if (!node) return 'unknown';
  if (node.$ref) return refName(node.$ref);
  if (node.enum) {
    const values = node.enum as unknown[];
    const shown = values.slice(0, 4).map(v => String(v));
    const suffix = values.length > 4 ? ' | …' : '';
    return `enum(${shown.join(' | ')}${suffix})`;
  }
  if (node.const !== undefined) return JSON.stringify(node.const);
  if (node.anyOf) return (node.anyOf as JsonSchemaNode[]).map(typeLabel).join(' | ');
  if (node.oneOf) return (node.oneOf as JsonSchemaNode[]).map(typeLabel).join(' | ');
  if (node.allOf && node.allOf.length === 1) return typeLabel(node.allOf[0]);
  if (node.type === 'array') return `${typeLabel(node.items)}[]`;
  if (Array.isArray(node.type)) return node.type.join(' | ');
  if (node.type === 'string' && node.format) return `string (${node.format})`;
  return node.type ?? 'object';
}

function scopeChipColor(
  scope: string | null | undefined,
): 'default' | 'info' | 'success' | 'warning' {
  if (scope === 'patient') return 'info';
  if (scope === 'organization') return 'success';
  if (scope === 'user') return 'warning';
  return 'default';
}

/**
 * Names of sub-models a property node references, recursing through the
 * wrappers Pydantic emits ($ref directly, or under items / anyOf / oneOf /
 * allOf / additionalProperties). These are keys into the schema's `$defs`.
 */
function collectRefs(node: JsonSchemaNode | undefined): string[] {
  if (!node || typeof node !== 'object') return [];
  const out: string[] = [];
  if (typeof node.$ref === 'string') out.push(refName(node.$ref));
  if (node.items) out.push(...collectRefs(node.items));
  if (node.additionalProperties) out.push(...collectRefs(node.additionalProperties));
  for (const key of ['anyOf', 'oneOf', 'allOf'] as const) {
    if (Array.isArray(node[key])) {
      for (const sub of node[key] as JsonSchemaNode[]) out.push(...collectRefs(sub));
    }
  }
  return [...new Set(out)];
}

/**
 * Renders a property's type. When the type references one or more sub-models
 * present in `$defs`, the reference is clickable to drill into that sub-model's
 * own schema.
 */
function SchemaTypeCell({
  prop,
  defs,
  onNavigate,
}: {
  prop: JsonSchemaNode;
  defs: Record<string, JsonSchemaNode>;
  onNavigate: (name: string) => void;
}) {
  const label = typeLabel(prop);
  const refs = collectRefs(prop).filter(name => name in defs);

  if (refs.length === 0) {
    return (
      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
        {label}
      </Typography>
    );
  }
  if (refs.length === 1) {
    return (
      <Link
        component="button"
        type="button"
        onClick={() => onNavigate(refs[0])}
        sx={{ fontFamily: 'monospace', fontSize: '0.75rem', textAlign: 'left' }}
      >
        {label}
      </Link>
    );
  }
  // Union of several sub-models: keep the plain label, add a chip per model.
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
        {label}
      </Typography>
      {refs.map(name => (
        <Chip
          key={name}
          label={name}
          size="small"
          variant="outlined"
          clickable
          onClick={() => onNavigate(name)}
          sx={{ height: 18, fontSize: '0.7rem' }}
        />
      ))}
    </Box>
  );
}

export default function CortexResourcesPage() {
  const [search, setSearch] = useState('');
  // Selected type + drill path live in the URL so the browser/back button walks
  // them (and the view is shareable): ?type=encounter&sub=Foo/Bar.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedType = searchParams.get('type');
  const subPath = useMemo(() => {
    const raw = searchParams.get('sub');
    return raw ? raw.split('/').filter(Boolean) : [];
  }, [searchParams]);

  const selectType = (type: string) => setSearchParams({ type });
  const setSubPath = (next: string[]) =>
    setSearchParams(
      next.length ? { type: selectedType ?? '', sub: next.join('/') } : { type: selectedType ?? '' },
    );

  const { data, isLoading, error } = useListResourceTypesQuery();

  const items = useMemo(() => {
    const all = data?.items ?? [];
    if (!search.trim()) return all;
    const needle = search.trim().toLowerCase();
    return all.filter(
      t =>
        t.resource_type.toLowerCase().includes(needle) ||
        t.model.toLowerCase().includes(needle),
    );
  }, [data, search]);

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>

        <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
          <Box>
            <h1 className="text-2xl font-bold text-gray-900">Resource Types</h1>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Every resource type registered with the Resource Access registry,
              with its full schema contract. Coverage grows as services adopt
              the common interface.
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Filter types…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />
        </div>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load the resource type catalog. Try refreshing.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Paper sx={{ overflow: 'hidden', flex: '0 1 380px', minWidth: 300 }}>
            <TableContainer sx={{ maxHeight: '70vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Scope</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2" color="text.secondary">
                            Loading catalog…
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          {search ? 'No types match the filter.' : 'No registered resource types.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {items.map(t => (
                    <TableRow
                      key={t.resource_type}
                      hover
                      selected={t.resource_type === selectedType}
                      onClick={() => selectType(t.resource_type)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                          {t.resource_type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{t.model}</Typography>
                      </TableCell>
                      <TableCell>
                        {t.scope ? (
                          <Chip
                            label={t.scope}
                            color={scopeChipColor(t.scope)}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Box sx={{ flex: '1 1 480px', minWidth: 360 }}>
            {selectedType ? (
              <SchemaPanel
                key={selectedType}
                resourceType={selectedType}
                path={subPath}
                onPathChange={setSubPath}
              />
            ) : (
              <Paper sx={{ p: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Select a resource type to view its schema.
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      </main>
    </div>
  );
}

function SchemaPanel({
  resourceType,
  path,
  onPathChange,
}: {
  resourceType: string;
  // Drill path into nested sub-models ($defs); empty = the root model.
  path: string[];
  onPathChange: (next: string[]) => void;
}) {
  const [view, setView] = useState<'fields' | 'json'>('fields');
  const { data, isLoading, error } = useGetResourceTypeSchemaQuery({ resourceType });

  const rootSchema: JsonSchemaNode = data?.json_schema ?? {};
  const defs: Record<string, JsonSchemaNode> = rootSchema.$defs ?? {};

  const atRoot = path.length === 0;
  const activeNode: JsonSchemaNode = atRoot
    ? rootSchema
    : defs[path[path.length - 1]] ?? {};
  const properties: Record<string, JsonSchemaNode> = activeNode.properties ?? {};
  const required = new Set<string>(activeNode.required ?? []);
  // The Resource Protocol's system-managed fields apply to the root model only.
  const systemManaged = new Set<string>(
    atRoot ? data?.system_managed_fields ?? [] : [],
  );

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {atRoot ? (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                {resourceType}
              </Typography>
              {data && (
                <Typography variant="caption" color="text.secondary">
                  Model: <code>{data.model}</code>
                </Typography>
              )}
            </>
          ) : (
            <Breadcrumbs sx={{ fontSize: '0.85rem' }}>
              <Link
                component="button"
                type="button"
                onClick={() => onPathChange([])}
                sx={{ fontFamily: 'monospace' }}
              >
                {resourceType}
              </Link>
              {path.map((name, i) =>
                i === path.length - 1 ? (
                  <Typography
                    key={name}
                    variant="subtitle2"
                    sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                  >
                    {name}
                  </Typography>
                ) : (
                  <Link
                    key={name}
                    component="button"
                    type="button"
                    onClick={() => onPathChange(path.slice(0, i + 1))}
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {name}
                  </Link>
                ),
              )}
            </Breadcrumbs>
          )}
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, v) => v && setView(v)}
        >
          <ToggleButton value="fields">Fields</ToggleButton>
          <ToggleButton value="json">Raw JSON</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading schema…
          </Typography>
        </Box>
      )}

      {!!error && (
        <Alert severity="error" sx={{ m: 2 }}>
          Failed to load the schema for this type.
        </Alert>
      )}

      {!isLoading && data && view === 'fields' && (
        <TableContainer sx={{ maxHeight: '62vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Required</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(properties).map(([name, prop]) => (
                <TableRow key={name}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {name}
                      </Typography>
                      {systemManaged.has(name) && (
                        <Tooltip title="Set by the system (Resource Protocol field) — not client-writable.">
                          <Chip label="system" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.7rem' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <SchemaTypeCell
                      prop={prop}
                      defs={defs}
                      onNavigate={name => onPathChange([...path, name])}
                    />
                  </TableCell>
                  <TableCell>
                    {required.has(name) ? (
                      <Chip label="required" color="info" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.7rem' }} />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        optional
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {prop.description ?? ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {Object.keys(properties).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      This model has no documented fields.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isLoading && data && view === 'json' && (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            maxHeight: '62vh',
            overflow: 'auto',
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            bgcolor: 'grey.50',
          }}
        >
          {JSON.stringify(activeNode, null, 2)}
        </Box>
      )}
    </Paper>
  );
}
